import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { showError, showSuccess } from '../utils/alerts';
import { API_BASE_URL } from '../config';
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaToggleOn, FaToggleOff, FaKey } from 'react-icons/fa';
import '../styles/pages/CredencialesMediosPagoPage.css';

const TIPOS_MEDIO_PAGO = [
    { value: 'sumup', label: 'SumUp' },
    { value: 'getnet', label: 'Getnet' },
    { value: 'tuu', label: 'TUU' },
    { value: 'mercadopago', label: 'Mercado Pago' },
    { value: 'redelcom', label: 'Redelcom' },
    { value: 'compraqui', label: 'Compraqui' }
];

const CredencialesMediosPagoPage = ({ token, id_company, company }) => {
    const navigate = useNavigate();
    const [credenciales, setCredenciales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        tipo_medio_pago: '',
        usuario: '',
        password: '',
        api_key: ''
    });

    const fetchCredenciales = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/credenciales-medios-pago`, {
                params: { id_company },
                headers: { Authorization: `Bearer ${token}` }
            });
            setCredenciales(response.data.data || []);
        } catch (error) {
            console.error('Error al obtener credenciales:', error);
            showError('Error al cargar las credenciales');
        } finally {
            setLoading(false);
        }
    }, [id_company, token]);

    useEffect(() => {
        if (id_company && token) {
            fetchCredenciales();
        }
    }, [id_company, token, fetchCredenciales]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData({ tipo_medio_pago: '', usuario: '', password: '', api_key: '' });
        setEditingId(null);
        setShowForm(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.tipo_medio_pago) {
            showError('Selecciona un tipo de medio de pago');
            return;
        }

        try {
            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/credenciales-medios-pago/${editingId}`, {
                    usuario: formData.usuario,
                    password: formData.password || undefined,
                    api_key: formData.api_key || undefined
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showSuccess('Credencial actualizada exitosamente');
            } else {
                await axios.post(`${API_BASE_URL}/api/credenciales-medios-pago`, {
                    id_company,
                    tipo_medio_pago: formData.tipo_medio_pago,
                    usuario: formData.usuario,
                    password: formData.password,
                    api_key: formData.api_key
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                showSuccess('Credencial creada exitosamente');
            }

            resetForm();
            fetchCredenciales();
        } catch (error) {
            console.error('Error:', error);
            showError(error.response?.data?.error || 'Error al guardar la credencial');
        }
    };

    const handleEdit = (credencial) => {
        setFormData({
            tipo_medio_pago: credencial.tipo_medio_pago,
            usuario: credencial.usuario || '',
            password: '',
            api_key: ''
        });
        setEditingId(credencial.id);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Estás seguro de eliminar esta credencial?')) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/credenciales-medios-pago/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess('Credencial eliminada');
            fetchCredenciales();
        } catch (error) {
            console.error('Error:', error);
            showError('Error al eliminar la credencial');
        }
    };

    const handleToggle = async (id) => {
        try {
            const response = await axios.patch(`${API_BASE_URL}/api/credenciales-medios-pago/${id}/toggle`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showSuccess(response.data.message);
            fetchCredenciales();
        } catch (error) {
            console.error('Error:', error);
            showError('Error al cambiar el estado');
        }
    };

    const getTipoLabel = (tipo) => {
        const found = TIPOS_MEDIO_PAGO.find(t => t.value === tipo);
        return found ? found.label : tipo;
    };

    const tiposDisponibles = TIPOS_MEDIO_PAGO.filter(tipo =>
        !credenciales.some(c => c.tipo_medio_pago === tipo.value) ||
        (editingId && credenciales.find(c => c.id === editingId)?.tipo_medio_pago === tipo.value)
    );

    return (
        <div className="credenciales-page">
            <div className="credenciales-header">
                <button className="btn-back" onClick={() => navigate('/dashboard')}>
                    <FaArrowLeft /> Volver al Dashboard
                </button>
                <h1>Configurar Medios de Pago</h1>
                {company && <p className="company-name">{company.name}</p>}
            </div>

            <div className="credenciales-content">
                <div className="credenciales-actions">
                    <button
                        className="btn-primary"
                        onClick={() => { resetForm(); setShowForm(!showForm); }}
                        disabled={tiposDisponibles.length === 0 && !showForm}
                    >
                        <FaPlus /> {showForm ? 'Cancelar' : 'Agregar Credencial'}
                    </button>
                </div>

                {showForm && (
                    <div className="credenciales-form-container">
                        <form onSubmit={handleSubmit} className="credenciales-form">
                            <h3>{editingId ? 'Editar Credencial' : 'Nueva Credencial'}</h3>

                            <div className="form-group">
                                <label>Tipo de Medio de Pago *</label>
                                <select
                                    name="tipo_medio_pago"
                                    value={formData.tipo_medio_pago}
                                    onChange={handleInputChange}
                                    disabled={!!editingId}
                                    required
                                >
                                    <option value="">Seleccionar...</option>
                                    {tiposDisponibles.map(tipo => (
                                        <option key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Usuario</label>
                                    <input
                                        type="text"
                                        name="usuario"
                                        value={formData.usuario}
                                        onChange={handleInputChange}
                                        placeholder="Usuario de acceso"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Contraseña</label>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder={editingId ? 'Dejar vacío para mantener' : 'Contraseña'}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>API Key Privada (opcional)</label>
                                <input
                                    type="password"
                                    name="api_key"
                                    value={formData.api_key}
                                    onChange={handleInputChange}
                                    placeholder={editingId ? 'Dejar vacío para mantener' : 'API Key privada del proveedor (ej: sup_sk_...)'}
                                />
                                <small className="form-hint">Solo si el proveedor requiere una API Key privada para consultas</small>
                            </div>

                            <div className="form-actions">
                                <button type="submit" className="btn-success">
                                    {editingId ? 'Actualizar' : 'Guardar'}
                                </button>
                                <button type="button" className="btn-secondary" onClick={resetForm}>
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="credenciales-table-container">
                    {loading ? (
                        <p className="loading">Cargando...</p>
                    ) : credenciales.length === 0 ? (
                        <div className="no-data">
                            <p>No hay credenciales configuradas.</p>
                            <p>Agrega una credencial para poder consultar transacciones directamente desde la API del proveedor.</p>
                        </div>
                    ) : (
                        <table className="credenciales-table">
                            <thead>
                                <tr>
                                    <th>Medio de Pago</th>
                                    <th>Usuario</th>
                                    <th>API Key</th>
                                    <th>Estado</th>
                                    <th>Fecha Creación</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {credenciales.map(cred => (
                                    <tr key={cred.id} className={!cred.activo ? 'inactive' : ''}>
                                        <td>
                                            <span className="tipo-badge">{getTipoLabel(cred.tipo_medio_pago)}</span>
                                        </td>
                                        <td>{cred.usuario || '-'}</td>
                                        <td>
                                            {cred.tiene_api_key ? (
                                                <span className="api-key-badge"><FaKey /> Configurada</span>
                                            ) : (
                                                <span className="no-api-key">-</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${cred.activo ? 'active' : 'inactive'}`}>
                                                {cred.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td>{new Date(cred.fecha_creacion).toLocaleDateString('es-CL')}</td>
                                        <td className="actions">
                                            <button
                                                className="btn-icon btn-toggle"
                                                onClick={() => handleToggle(cred.id)}
                                                title={cred.activo ? 'Desactivar' : 'Activar'}
                                            >
                                                {cred.activo ? <FaToggleOn /> : <FaToggleOff />}
                                            </button>
                                            <button
                                                className="btn-icon btn-edit"
                                                onClick={() => handleEdit(cred)}
                                                title="Editar"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="btn-icon btn-delete"
                                                onClick={() => handleDelete(cred.id)}
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="credenciales-info">
                    <h4>Información</h4>
                    <ul>
                        <li><strong>Usuario y Contraseña:</strong> Credenciales de acceso al portal del proveedor</li>
                        <li><strong>API Key Privada:</strong> Clave secreta para consultas API (ej: SumUp usa sup_sk_...)</li>
                        <li>Las API Keys públicas se configuran a nivel de aplicación en el servidor.</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default CredencialesMediosPagoPage;
